// useCityServiceCheck.js
// Auto-detect-only hook: takes a geolocation-resolved location, hits the
// real check-service API (LoyaltyController::cityService with `city` set),
// and reports whether we currently service it.
//
// Manual selections (typed search, popular/suggested city clicks) do NOT
// use this hook - they're verified locally against the store-backed city
// directory instead. See useCityDirectory's isCityServiceable.
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkCityService, resetCityCheck } from "../store/citySlices/cityServiceSlice";

export default function useCityServiceCheck() {
  const dispatch = useDispatch();
  const checking = useSelector((state) => state.cityService.checking);
  const notServiceable = useSelector((state) => state.cityService.notServiceable);

  // Resolves to { serviceable, matchedCity }. `matchedCity` is the backend's
  // static_cities match (id, cityName, fullRegionName) when serviceable - the
  // authoritative store-backed city name, as opposed to whatever Google's
  // Geocoder parsed out of the address on the frontend. `matchedCity` is null
  // when unserviceable, unresolved, or the check itself failed.
  const checkCity = useCallback(
    async (locationData) => {
      dispatch(resetCityCheck());

      if (!locationData?.city) {
        // No city could be resolved from the address - don't block on it.
        return { serviceable: true, matchedCity: null };
      }

      const result = await dispatch(
        checkCityService({
          city: locationData.city,
          state: locationData.state,
          country: locationData.country,
        })
      );

      if (checkCityService.rejected.match(result)) {
        // Don't block the user if the availability check itself fails.
        console.error("City service check failed:", result.payload);
        return { serviceable: true, matchedCity: null };
      }

      const data = result.payload?.data;
      return {
        serviceable: Boolean(data?.serviceable),
        matchedCity: data?.matchedCity ?? null,
      };
    },
    [dispatch]
  );

  const reset = useCallback(() => dispatch(resetCityCheck()), [dispatch]);

  return { checking, notServiceable, checkCity, reset };
}